import React, { useCallback, useEffect, useRef, useState } from 'react';
import GoogleMapReact from 'google-map-react';
import { isMobile } from 'react-device-detect';

import Constants from '_app/constants';
import { MapControls } from './MapControls';
import { DrawingPanel } from './DrawingPanel';
import { Marker } from './Marker';
import Utils from '_app/utils';

import { Env } from '_app/config';

const toMapLatLng = (maps, coords = []) => coords.map((coord) => new maps.LatLng(coord.lat, coord.lng));

export const MapBoard = ({
  items = [],
  preSelected = null,
  mapCenter,
  mapZoom,
  isMapDrawing = false,
  bounds = [],
  mapReady = false,
  loading = false,
  success = false,
  searchAsMove = true,
  toggleList = false,
  onMapReady = () => {},
  onMapChange = () => {},
  onMarkerClick = () => {},
  onUpdateBounds = () => {},
  onToggleSearchAsMove = () => {},
  onZoomIn = () => {},
  onZoomOut = () => {},
  onClear = () => {},
  onSearchCurrentBounds = () => {},
}) => {
  const [mapType, setMapType] = useState(Constants.GoogleMapTypes.ROADMAP);
  const mapRef = useRef(null);
  const mapsRef = useRef(null);
  const polygonsRef = useRef([]);

  useEffect(() => {
    if (mapReady && mapRef.current && mapsRef.current && !loading && success && items.length && !toggleList) {
      const coords = items.map(({ latitude, longitude }) => new mapsRef.current.LatLng(latitude, longitude));
      Utils.fitMapToBounds(mapsRef.current, mapRef.current, coords);
    }
  }, [loading, success, toggleList]);

  useEffect(() => {
    if (!mapRef.current) {
      return;
    }
    if (isMapDrawing) {
      mapRef.current.setOptions({ draggableCursor: 'crosshair' });
    } else {
      mapRef.current.setOptions({ draggableCursor: '' });
    }
  }, [isMapDrawing]);

  useEffect(() => {
    if (!mapReady || !mapsRef.current) {
      return;
    }

    cleanPolygon();
    bounds.forEach((bound) => {
      const coords = toMapLatLng(mapsRef.current, bound);
      polygonsRef.current.push(Utils.createPolygon(mapRef.current, mapsRef.current, new mapsRef.current.MVCArray(coords), true));
    });
  }, [bounds, mapReady]);

  const handleApiLoaded = useCallback(
    (mapInstance, mapsInstance) => {
      mapRef.current = mapInstance;
      mapsRef.current = mapsInstance;
      onMapReady();
    },
    [onMapReady],
  );

  const cleanPolygon = () => {
    if (polygonsRef.current.length) {
      polygonsRef.current.forEach((polygon) => {
        polygon.setMap(null);
      });
      polygonsRef.current = [];
    }
  };

  const drawFinalPolygon = (coords, shouldUpdateApi = true) => {
    if (!mapRef.current || !mapsRef.current) {
      return;
    }
    const nextBounds = [...bounds, coords.getArray().map((coord) => ({ lat: coord.lat(), lng: coord.lng() }))];
    onUpdateBounds(nextBounds, shouldUpdateApi);
  };

  return (
    <div className="flex-1 relative">
      <GoogleMapReact
        bootstrapURLKeys={{ key: Env.GOOGLE_API_KEY }}
        yesIWantToUseGoogleMapApiInternals={true}
        center={mapCenter}
        zoom={mapZoom}
        options={() => ({
          panControl: false,
          zoomControl: false,
          fullscreenControl: false,
          mapTypeControl: false,
          mapTypeId: mapType,
        })}
        onGoogleApiLoaded={({ map, maps }) => handleApiLoaded(map, maps)}
        onChange={(data) => onMapChange(data)}
      >
        {items.map((item) => (
          <Marker
            key={item.mlsnum}
            lat={item.latitude}
            lng={item.longitude}
            mlsnum={item.mlsnum}
            preSelected={preSelected && preSelected.mlsnum === item.mlsnum}
            price={item.price}
            onClick={() => onMarkerClick(item, isMobile)}
          />
        ))}
        {mapReady && isMapDrawing && (
          <DrawingPanel map={mapRef.current} maps={mapsRef.current} onStartDrawing={() => {}} onCompleteDrawing={drawFinalPolygon} />
        )}
      </GoogleMapReact>
      {!toggleList && (
        <MapControls
          searchAsMove={searchAsMove}
          enableMapMoveSearch={onToggleSearchAsMove}
          onChangeMapType={setMapType}
          loading={loading}
          onZoomIn={onZoomIn}
          onZoomOut={onZoomOut}
          onClear={() => {
            cleanPolygon();
            onClear();
          }}
          searchCurrentBounds={onSearchCurrentBounds}
        />
      )}
    </div>
  );
};
